import axios from "axios";
import { createHttpService } from "../ApiService";
import {
  TCreateObepRecordRequest,
  TCreateObepRecordResponse,
  TObepListFilters,
  TObepRecord,
  TUpdateObepRecordRequest,
} from "./obep.interfaces";
import { obepListMock } from "../../__mocks__/obep/obepMock";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;

type WireInstitute = {
  _id?: string;
  id?: string;
  instituteName?: string;
  name?: string;
};

type WireCreatedBy = {
  _id?: string;
  id?: string;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  userName?: string;
};

function normalizeInstituteRef(
  institute: unknown,
  fallback?: TObepRecord["institute"],
): TObepRecord["institute"] {
  const inst = institute as WireInstitute | undefined;
  return {
    id: inst?._id ?? inst?.id ?? fallback?.id,
    name: inst?.instituteName ?? inst?.name ?? fallback?.name,
  };
}

function normalizeCreatedByRef(
  createdBy: unknown,
  fallback?: TObepRecord["createdBy"],
): TObepRecord["createdBy"] {
  const cb = createdBy as WireCreatedBy | undefined;
  const authorName =
    cb &&
    (cb.firstName || cb.lastName || cb.userName)
      ? [cb.firstName, cb.lastName].filter(Boolean).join(" ").trim() ||
        cb.userName
      : undefined;
  return {
    id: cb?._id ?? cb?.id ?? fallback?.id,
    name: authorName ?? fallback?.name,
  };
}

function normalizeObepRecord(raw: TObepRecord): TObepRecord {
  return {
    ...raw,
    institute: normalizeInstituteRef(raw.institute, raw.institute),
    createdBy: normalizeCreatedByRef(raw.createdBy, raw.createdBy),
  };
}

/** OpenAPI `CreateObepRecordResponse` uses the same nested DTOs as GET (`ObepInstituteRefDto`, etc.). */
function normalizeCreateObepRecordResponse(
  raw: TCreateObepRecordResponse,
): TCreateObepRecordResponse {
  return {
    ...raw,
    institute: normalizeInstituteRef(raw.institute, raw.institute),
    createdBy: normalizeCreatedByRef(raw.createdBy, raw.createdBy),
  };
}

/** Serialize exactly as OpenAPI `CreateObepRecordRequest` (camelCase, includes `instituteRef`). */
function toCreateObepRecordWireBody(request: TCreateObepRecordRequest) {
  return {
    name: request.name,
    description: request.description,
    objectives: request.objectives,
    measurements: request.measurements,
    instituteRef: request.instituteRef,
    createdBy: request.createdBy,
  };
}

/** Serialize as OpenAPI `UpdateObepRecordRequest`: omit undefined; empty objectives array is omitted. */
function toUpdateObepRecordWireBody(request: TUpdateObepRecordRequest) {
  const body: Record<string, unknown> = {};
  if (request.name !== undefined) body.name = request.name;
  if (request.description !== undefined) body.description = request.description;
  if (request.measurements !== undefined) body.measurements = request.measurements;
  if (request.objectives !== undefined && request.objectives.length > 0) {
    body.objectives = request.objectives;
  }
  return body;
}

export const useObepService = () => {
  const httpAPI = createHttpService();
  const basePath = "/outcome-based-edu-policy";

  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";
  const shouldUseMock = appWithMockData;

  const getObepRecords = (filters?: TObepListFilters) => {
    const page = filters?.page ?? DEFAULT_PAGE;
    const limit = filters?.limit ?? DEFAULT_LIMIT;
    const instituteId = filters?.instituteId;

    if (shouldUseMock) {
      let rows = obepListMock;
      if (instituteId) {
        rows = rows.filter((r) => r.institute.id === instituteId);
      }
      const start = (page - 1) * limit;
      return Promise.resolve(
        rows.slice(start, start + limit).map(normalizeObepRecord),
      );
    }
    return httpAPI.academicsRoot
      .get<TObepRecord[]>(basePath, {
        params: {
          page,
          limit,
          ...(instituteId ? { instituteId } : {}),
        },
      })
      .then((rows) => rows.map(normalizeObepRecord))
      .catch((err: unknown) => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return [];
        }
        throw err;
      });
  };

  const getObepRecordById = (obepId: string) => {
    if (shouldUseMock) {
      const record = obepListMock.find((r) => r.obepId === obepId);
      if (!record) {
        return Promise.reject(new Error("OBEP record not found"));
      }
      return Promise.resolve(record);
    }
    return httpAPI.academicsRoot
      .get<TObepRecord>(`${basePath}/by-id/${obepId}`)
      .then(normalizeObepRecord);
  };

  const createObepRecord = (request: TCreateObepRecordRequest) => {
    if (shouldUseMock) {
      const now = new Date().toISOString();
      const obepId = `obep-${Date.now()}`;
      const newRecord: TObepRecord = {
        obepId,
        obepName: request.name,
        measurements: request.measurements,
        institute: { id: request.instituteRef },
        createdBy: { id: request.createdBy },
        createdAt: now,
        description: request.description,
        objectives: request.objectives,
      };
      obepListMock.unshift(newRecord);
      const response: TCreateObepRecordResponse = {
        obepId,
        obepName: request.name,
        measurements: request.measurements,
        institute: { id: request.instituteRef },
        createdBy: { id: request.createdBy },
        createdAt: now,
        message: "OBEP record created successfully",
      };
      return Promise.resolve(response);
    }
    return httpAPI.academicsRoot
      .post<TCreateObepRecordResponse>(basePath, toCreateObepRecordWireBody(request))
      .then(normalizeCreateObepRecordResponse);
  };

  const updateObepRecord = (
    obepRecordId: string,
    request: TUpdateObepRecordRequest,
  ) => {
    const wireBody = toUpdateObepRecordWireBody(request);
    if (Object.keys(wireBody).length === 0) {
      return Promise.reject(
        new Error("Update requires at least one field to change."),
      );
    }

    if (shouldUseMock) {
      const idx = obepListMock.findIndex((r) => r.obepId === obepRecordId);
      if (idx === -1) {
        return Promise.reject(new Error("OBEP record not found"));
      }
      const prev = obepListMock[idx];
      const next: TObepRecord = {
        ...prev,
        ...(request.name !== undefined ? { obepName: request.name } : {}),
        ...(request.measurements !== undefined
          ? { measurements: request.measurements }
          : {}),
        ...(request.description !== undefined
          ? { description: request.description }
          : {}),
        ...(request.objectives !== undefined
          ? { objectives: request.objectives }
          : {}),
      };
      obepListMock[idx] = next;
      return Promise.resolve(normalizeObepRecord(next));
    }

    return httpAPI.academicsRoot
      .patch<TObepRecord>(
        `${basePath}/by-id/${obepRecordId}`,
        wireBody,
      )
      .then(normalizeObepRecord);
  };

  return {
    getObepRecords,
    getObepRecordById,
    createObepRecord,
    updateObepRecord,
  };
};
